import { Router } from "express";

import { quizController } from "../controllers/quiz-controller";
import { quizReportController } from "../controllers/quiz-report-controller";
import {
  authenticate,
  requireEmailVerification,
} from "../middleware/auth-middleware";
import { requireRole } from "../middleware/role-middleware";
import {
  attachSchoolContext,
  requireSchoolContext,
} from "../middleware/school-context-middleware";
import { uploadQuizMaterial } from "../middleware/upload-middleware";
import {
  createQuizValidation,
  gradeQuizAttemptValidation,
  updateQuizValidation,
} from "../validation/quiz-validation";

const router = Router();

router.use(authenticate);
router.use(requireEmailVerification);
router.use(requireRole(["school", "teacher"]));
router.use(attachSchoolContext);

/**
 * @route   POST /api/v1/school/quizzes
 * @desc    Create a new quiz (with optional quizMaterial upload)
 * @access  Private (School Admin, Teacher)
 */
router.post("/", requireSchoolContext, uploadQuizMaterial, async (req, res) => {
  // Parse questions if string (multipart form data)
  if (typeof req.body.questions === "string") {
    try {
      req.body.questions = JSON.parse(req.body.questions);
    } catch {
      return res.status(400).json({
        success: false,
        message: "Invalid questions format",
        error: "Questions must be a valid JSON array",
      });
    }
  }

  const { error } = createQuizValidation.validate(req.body);
  if (error) {
    return res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details[0].message,
    });
  }
  return quizController.createQuiz(req, res);
});

/**
 * @route   GET /api/v1/school/quizzes
 * @desc    Get all quizzes for school (filter by class/semester)
 * @access  Private (School Admin, Teacher)
 * @query   classId, semesterId, status
 */
router.get("/", requireSchoolContext, (req, res) =>
  quizController.getAllQuizzes(req, res),
);

/**
 * @route   GET /api/v1/school/quizzes/:id
 * @desc    Get quiz by ID (with answers - teacher view)
 * @access  Private (School Admin, Teacher)
 */
router.get("/:id", requireSchoolContext, (req, res) =>
  quizController.getQuizById(req, res),
);

/**
 * @route   PUT /api/v1/school/quizzes/:id
 * @desc    Update quiz (with optional quizMaterial upload)
 * @access  Private (School Admin, Teacher)
 */
router.put(
  "/:id",
  requireSchoolContext,
  uploadQuizMaterial,
  async (req, res) => {
    if (typeof req.body.questions === "string") {
      try {
        req.body.questions = JSON.parse(req.body.questions);
      } catch {
        return res.status(400).json({
          success: false,
          message: "Invalid questions format",
          error: "Questions must be a valid JSON array",
        });
      }
    }

    const { error } = updateQuizValidation.validate(req.body);
    if (error) {
      return res.status(400).json({
        success: false,
        message: "Validation failed",
        error: error.details[0].message,
      });
    }
    return quizController.updateQuiz(req, res);
  },
);

/**
 * @route   DELETE /api/v1/school/quizzes/:id
 * @desc    Delete quiz
 * @access  Private (School Admin, Teacher)
 */
router.delete("/:id", requireSchoolContext, (req, res) =>
  quizController.deleteQuiz(req, res),
);

/**
 * @route   POST /api/v1/school/quizzes/:id/publish
 * @desc    Publish quiz
 * @access  Private (School Admin, Teacher)
 */
router.post("/:id/publish", requireSchoolContext, (req, res) =>
  quizController.publishQuiz(req, res),
);

/**
 * @route   POST /api/v1/school/quizzes/:id/close
 * @desc    Close quiz (no more attempts)
 * @access  Private (School Admin, Teacher)
 */
router.post("/:id/close", requireSchoolContext, (req, res) =>
  quizController.closeQuiz(req, res),
);

/**
 * @route   GET /api/v1/school/quizzes/:id/attempts
 * @desc    Get all attempts for a quiz
 * @access  Private (School Admin, Teacher)
 * @query   status
 */
router.get("/:id/attempts", requireSchoolContext, (req, res) =>
  quizController.getQuizAttempts(req, res),
);

/**
 * @route   PUT /api/v1/school/quizzes/:id/attempts/:attemptId/grade
 * @desc    Manually grade quiz attempt (for essay/short answer questions)
 * @access  Private (School Admin, Teacher)
 */
router.put(
  "/:id/attempts/:attemptId/grade",
  requireSchoolContext,
  async (req, res) => {
    const { error } = gradeQuizAttemptValidation.validate(req.body);
    if (error) {
      return res.status(400).json({
        success: false,
        message: "Validation failed",
        error: error.details[0].message,
      });
    }
    return quizController.gradeQuizAttempt(req, res);
  },
);

/**
 * @route   GET /api/v1/school/quizzes/:id/report
 * @desc    Get comprehensive quiz report (all students' performance)
 * @access  Private (School Admin, Teacher)
 */
router.get("/:id/report", requireSchoolContext, (req, res) =>
  quizReportController.getQuizReport(req, res),
);

/**
 * @route   GET /api/v1/school/quiz-statistics
 * @desc    Get overall quiz statistics for school
 * @access  Private (School Admin)
 */
router.get("/statistics", requireSchoolContext, (req, res) =>
  quizReportController.getSchoolQuizStatistics(req, res),
);

export default router;
